import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Calendar, CalendarDays, CalendarRange, TrendingUp, Building2, Filter,
  CheckSquare, Square, Wallet, Receipt, CalendarClock, CalendarCheck, Tag,
} from 'lucide-react';
import { toast } from 'sonner';
import SAR from '../components/SAR';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const today = () => new Date().toISOString().split('T')[0];
const fmt = (n) => Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 2 });

const pctColor = (pct) => {
  if (pct >= 100) return '#10B981';
  if (pct >= 75) return '#84CC16';
  if (pct >= 50) return '#F59E0B';
  if (pct >= 25) return '#F97316';
  return '#EF4444';
};

const TotalCard = ({ icon: Icon, accent, label, block, testId }) => {
  if (!block) return null;
  const {
    target = 0, achieved = 0, remaining = 0, percentage = 0, transactions = 0,
    discount_achieved = 0, discount_transactions = 0, discount_percentage = 0,
    by_branch = [],
  } = block;
  return (
    <Card
      className="p-5 relative overflow-hidden"
      style={{ borderTop: `4px solid ${accent}` }}
      data-testid={testId}
    >
      <div
        className="absolute top-0 left-0 h-24 w-24 rounded-full -translate-x-12 -translate-y-12 opacity-10"
        style={{ background: accent }}
      />
      <div className="flex items-center gap-3 mb-3">
        <div className="h-10 w-10 rounded-lg flex items-center justify-center" style={{ background: `${accent}1A`, color: accent }}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <p className="text-xs text-foreground/55">إجمالي مبيعات</p>
          <h3 className="font-bold text-base">{label}</h3>
        </div>
      </div>

      <p className="text-3xl font-black font-numbers leading-none" style={{ color: accent }} data-testid={`${testId}-achieved`}>
        {fmt(achieved)} <SAR className="text-xl" />
      </p>
      <div className="flex items-center gap-1 mt-1 text-xs text-foreground/55">
        <Receipt className="h-3 w-3" />
        <span className="font-numbers">{transactions}</span> عملية
      </div>

      {/* Discount-code chip — visible only when there are discount sales */}
      {discount_achieved > 0 && (
        <div
          className="mt-3 px-3 py-2 rounded-md bg-amber-50 border border-amber-200 flex items-center justify-between"
          data-testid={`${testId}-discount`}
        >
          <div className="flex items-center gap-1.5 text-amber-800">
            <Tag className="h-3.5 w-3.5" />
            <span className="text-[11px] font-bold">إجمالي مبيعات كود الخصم</span>
          </div>
          <div className="text-right">
            <p className="text-sm font-black font-numbers text-amber-700">
              {fmt(discount_achieved)} <SAR className="text-[11px]" />
            </p>
            <p className="text-[10px] text-amber-700/80 font-numbers">
              {discount_transactions} عملية • {discount_percentage}% من الإجمالي
            </p>
          </div>
        </div>
      )}

      {/* Target / Remaining / Percentage row */}
      <div className="grid grid-cols-3 gap-2 mt-4 pb-3 border-b border-border" data-testid={`${testId}-kpis`}>
        <div className="text-center">
          <p className="text-[10px] text-foreground/50">المستهدف</p>
          <p className="text-xs font-numbers font-bold">{fmt(target)}</p>
        </div>
        <div className="text-center">
          <p className="text-[10px] text-foreground/50">المتبقي</p>
          <p className={`text-xs font-numbers font-bold ${remaining > 0 ? 'text-amber-700' : 'text-emerald-700'}`}>{fmt(remaining)}</p>
        </div>
        <div className="text-center">
          <p className="text-[10px] text-foreground/50">النسبة</p>
          <p className="text-xs font-numbers font-black" style={{ color: pctColor(percentage) }}>{percentage}%</p>
        </div>
      </div>

      {/* Progress bar */}
      <div className="mt-3 h-2 bg-accent rounded-full overflow-hidden">
        <div
          className="h-full transition-all duration-500"
          style={{ width: `${Math.min(percentage, 100)}%`, background: pctColor(percentage) }}
        />
      </div>

      {by_branch && by_branch.length > 0 && (
        <div className="mt-4 pt-4 border-t border-border space-y-2">
          {by_branch.map((b) => {
            const bPct = Math.min(b.percentage || 0, 999);
            return (
              <div key={b.branch_id} className="text-xs">
                <div className="flex items-center gap-2">
                  <span className="flex-1 font-bold truncate">{b.branch_name}</span>
                  <span className="font-numbers font-black" style={{ color: pctColor(b.percentage || 0) }}>{b.percentage}%</span>
                </div>
                <div className="flex items-center gap-1.5 text-[10px] text-foreground/55 mt-0.5 font-numbers">
                  <span>محقق <span className="font-bold text-emerald-700">{fmt(b.achieved)}</span></span>
                  <span className="text-foreground/30">·</span>
                  <span>مستهدف <span className="font-bold">{fmt(b.target)}</span></span>
                  <span className="text-foreground/30">·</span>
                  <span>متبقي <span className="font-bold text-amber-700">{fmt(b.remaining)}</span></span>
                </div>
                <div className="mt-1 h-1 bg-accent rounded-full overflow-hidden">
                  <div className="h-full" style={{ width: `${Math.min(bPct, 100)}%`, background: pctColor(b.percentage || 0) }} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
};

export default function SuperTotals() {
  const [branches, setBranches] = useState([]);
  const [selectedBranchIds, setSelectedBranchIds] = useState(null); // null = all
  const [useCustom, setUseCustom] = useState(false);
  const [startDate, setStartDate] = useState(today());
  const [endDate, setEndDate] = useState(today());
  // Period focus tabs — defaults to الأسبوع per super-admin preference
  const [focus, setFocus] = useState('week'); // today | yesterday | week | month | quarter | all
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Initial branches fetch
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/branches`);
        setBranches(res.data || []);
      } catch (e) {
        toast.error('فشل تحميل الفروع');
      }
    })();
  }, []);

  const fetchTotals = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedBranchIds && selectedBranchIds.length > 0) {
        params.append('branch_ids', selectedBranchIds.join(','));
      }
      if (useCustom) {
        params.append('start_date', startDate);
        params.append('end_date', endDate);
      }
      const res = await axios.get(`${API_URL}/api/super/totals?${params.toString()}`);
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'فشل تحميل البيانات');
    } finally {
      setLoading(false);
    }
  }, [selectedBranchIds, useCustom, startDate, endDate]);

  useEffect(() => { fetchTotals(); }, [fetchTotals]);

  const toggleBranch = (id) => {
    setSelectedBranchIds((prev) => {
      const current = prev || branches.map((b) => b.id);
      if (current.includes(id)) {
        const next = current.filter((x) => x !== id);
        return next.length === 0 ? branches.map((b) => b.id).filter((x) => x !== id) : next;
      }
      return [...current, id];
    });
  };

  const selectAll = () => setSelectedBranchIds(null);
  const deselectAll = () => setSelectedBranchIds([]);

  return (
    <div className="space-y-6" dir="rtl" data-testid="super-totals-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="h-12 w-12 rounded-xl bg-emerald-500/10 flex items-center justify-center">
          <Wallet className="h-6 w-6 text-emerald-700" />
        </div>
        <div>
          <h1 className="text-2xl sm:text-3xl font-black"><span className="font-brand">قِمَم</span> — المبيعات</h1>
          <p className="text-sm text-foreground/60 mt-1">ملخص سريع لمبيعات الشبكة (اليوم / أمس / الشهر) مع إمكانية اختيار فروع وتاريخ مخصص</p>
        </div>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="h-4 w-4 text-brand_blue" />
          <h3 className="font-bold text-sm">الفلاتر</h3>
        </div>

        {/* Branches multi-select */}
        <div className="space-y-2 mb-4">
          <div className="flex items-center justify-between">
            <Label className="text-sm">الفروع</Label>
            <div className="flex items-center gap-3">
              <button
                onClick={selectAll}
                className="text-xs text-brand_blue hover:underline"
                data-testid="totals-select-all-branches"
              >
                تحديد الكل
              </button>
              <button
                onClick={deselectAll}
                className="text-xs text-destructive hover:underline"
                data-testid="totals-deselect-all-branches"
              >
                إلغاء تحديد الكل
              </button>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {branches.map((b) => {
              const active = !selectedBranchIds || selectedBranchIds.includes(b.id);
              return (
                <button
                  key={b.id}
                  onClick={() => toggleBranch(b.id)}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-md border text-xs font-bold transition ${
                    active
                      ? 'bg-brand_blue/10 border-brand_blue/40 text-brand_blue'
                      : 'bg-white border-border text-foreground/50 hover:border-brand_blue/30'
                  }`}
                  data-testid={`totals-branch-toggle-${b.slug || b.id}`}
                >
                  {active ? <CheckSquare className="h-3.5 w-3.5" /> : <Square className="h-3.5 w-3.5" />}
                  <Building2 className="h-3.5 w-3.5" />
                  {b.name}
                </button>
              );
            })}
          </div>
        </div>

        {/* Custom date range */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-sm">تاريخ مخصص (اختياري)</Label>
            <button
              onClick={() => setUseCustom((v) => !v)}
              className="text-xs text-brand_blue hover:underline"
              data-testid="totals-toggle-custom"
            >
              {useCustom ? 'إخفاء التاريخ المخصص' : 'إضافة تاريخ مخصص'}
            </button>
          </div>
          {useCustom && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end pt-2" data-testid="totals-custom-range">
              <div className="space-y-1.5">
                <Label className="text-xs text-foreground/60">من</Label>
                <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="text-right font-numbers" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-foreground/60">إلى</Label>
                <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="text-right font-numbers" />
              </div>
              <Button onClick={fetchTotals} disabled={loading} className="gap-2">
                <Filter className="h-4 w-4" />
                {loading ? 'جاري...' : 'تطبيق'}
              </Button>
            </div>
          )}
        </div>
      </Card>

      {/* Period focus tabs — default الأسبوع */}
      {data && (
        <Card className="p-3 flex items-center gap-2 flex-wrap" data-testid="totals-focus-tabs">
          <span className="text-xs text-foreground/55 font-bold ms-1">تركيز على:</span>
          {[
            { k: 'today',     label: 'اليوم' },
            { k: 'yesterday', label: 'أمس' },
            { k: 'week',      label: 'الأسبوع' },
            { k: 'month',     label: 'الشهر' },
            { k: 'quarter',   label: 'الربع' },
            { k: 'all',       label: 'الكل' },
          ].map((t) => (
            <button
              key={t.k}
              onClick={() => setFocus(t.k)}
              data-testid={`totals-focus-${t.k}`}
              className={`text-xs font-bold px-3 py-1.5 rounded-md border transition-colors ${
                focus === t.k
                  ? 'bg-brand_blue text-white border-brand_blue'
                  : 'bg-white text-foreground/70 border-border hover:bg-accent/50'
              }`}
            >
              {t.label}
            </button>
          ))}
        </Card>
      )}

      {/* KPI cards */}
      {data ? (
        <>
          <div className={
            focus === 'all'
              ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'
              : 'grid grid-cols-1 gap-4 max-w-2xl'
          }>
            {(focus === 'all' || focus === 'today')     && <TotalCard icon={Calendar}      accent="#0F766E" label="اليوم"         block={data.today}     testId="totals-card-today" />}
            {(focus === 'all' || focus === 'yesterday') && <TotalCard icon={CalendarDays}  accent="#92400E" label="أمس"           block={data.yesterday} testId="totals-card-yesterday" />}
            {(focus === 'all' || focus === 'week')      && <TotalCard icon={CalendarClock} accent="#0EA5E9" label="الأسبوع"       block={data.week}      testId="totals-card-week" />}
            {(focus === 'all' || focus === 'month')     && <TotalCard icon={CalendarRange} accent="#1E40AF" label="الشهر الحالي" block={data.month}     testId="totals-card-month" />}
            {(focus === 'all' || focus === 'quarter')   && <TotalCard icon={CalendarCheck} accent="#CA8A04" label="الربع الحالي" block={data.quarter}   testId="totals-card-quarter" />}
            {data.custom && (
              <TotalCard icon={TrendingUp} accent="#B45309" label={data.custom.label} block={data.custom} testId="totals-card-custom" />
            )}
          </div>
        </>
      ) : (
        <Card className="p-12 text-center text-foreground/50">جاري التحميل...</Card>
      )}
    </div>
  );
}
